/* =================
   FILTERS BUILDER
   ================= */

import ExportSettings from '../../config/_export-settings';
import Globals from './_globals';
import { Rule } from '../../utils/_interfaces';

export default class FiltersBuilder {
	
	$elem: JQuery;
	$ruleSelector: JQuery;
	$ruleValueInput: JQuery;
	$rulesInput: JQuery;
	
	constructor(
		private $wrapper: JQuery,
		private fieldType: string,
		private settings: ExportSettings,
		private globals: Globals
	) {
		
		this.$elem = this.$wrapper.find('table');
		this.$ruleSelector = this.$elem.find('tfoot .sel-rule');
		this.$ruleValueInput = this.$elem.find('tfoot .sel-filter-value').find('input');
		this.$rulesInput = this.$elem.siblings('input:hidden');
		
		this.prepare();
		
	}
	
	prepare() {
		
		// Get the right conditions for the current field type.
		let conditionLabels: any = this.settings.get('conditionLabels');
		conditionLabels = $.inArray(this.fieldType, ['date', 'datetime', 'time']) > -1 ? conditionLabels['date'] : conditionLabels['default'];
		
		// Fill the rule selector with the coming values.
		for (const condition in conditionLabels) {
			this.$ruleSelector.append(`<option value="${ condition }">${ conditionLabels[condition] }</option>`);
		}
		
		this.bindEvents();
		
	}
	
	bindEvents() {
		
		this.$elem
		
			// Add rule button.
			.on('click', '.add-rule', () => this.addRule(this.$ruleSelector.val(), this.$ruleValueInput.val()) )
			
			// Remove rule button.
			.on('click', '.remove-rule', (evt: JQueryEventObject) => this.removeRule( $(evt.currentTarget).closest('tr') ))
			
			// Edit rule button.
			.on( 'click', '.edit-rule', (evt: JQueryEventObject) => this.editRule( $(evt.currentTarget).closest('tr') ) )
			
			// Save rule button.
			.on('click', '.save-rule', () => this.saveRule() )
			
			// Add/save the field filters when hitting enter on the filter value input.
			.on('keyup', '.sel-filter-value input', (evt: JQueryEventObject) => {
				if (evt.which === 13 ) { // Only when hitting the Enter key.
					$(evt.currentTarget).closest('tr').find('.sel-actions i:visible').click();
				}
			})
			
			// When selection a function condition, the value input should hide.
			.on('change', '.sel-rule', () => {
				
				const selectedRule: string = this.$ruleSelector.val();
				
				if ($.inArray(selectedRule, this.settings.get('noValueConditions')) > -1) {
					this.$ruleValueInput.val('');
					this.$ruleValueInput.css('visibility', 'hidden');
				}
				else {
					this.$ruleValueInput.css('visibility', 'visible');
				}
				
			});
		
	}
	
	/**
	 * Add a new rule to the field
	 *
	 * @param {string}        ruleSelected
	 * @param {string|number} value
	 */
	addRule(ruleSelected: string, value: string | number) {
		
		if (!ruleSelected) {
			this.showInvalidRuleMessage();
			return false;
		}
		
		// Add the rule to the table.
		this.$elem.find('tbody').append(`
			<tr>
				<td data-value="${ ruleSelected }">${ this.$ruleSelector.find(`option[value="${ ruleSelected }"]`).text() }</td>
				<td data-value="${ value }">${ value }</td>
				<td>
					<i class="edit-rule atum-icon atmi-pencil"></i>
					<i class="remove-rule atum-icon atmi-trash"></i>
				</td>
			</tr>
		`);
		
		this.reset();
		this.updateRulesInput();
		
	}
	
	/**
	 * Remove a rule from the field
	 *
	 * @param {JQuery} $row
	 */
	removeRule($row: JQuery) {
		
		$row.remove();
		this.updateRulesInput();
		
	}
	
	/**
	 * Edit a previously set rule
	 *
	 * @param {JQuery} $row
	 */
	editRule($row: JQuery) {
		
		// Disable edit.
		if ($row.hasClass('editing')) {
			
			this.$ruleSelector.val('').change();
			this.$ruleValueInput.val('');
			$row.removeClass('editing');
			this.$elem.find('tfoot tr').removeClass('editing');
			
		}
		// Enable edit.
		else {
			
			const rule: Rule = this.getRulefromRow( $row );
			
			this.$ruleSelector.val(rule.condition).change();
			this.$ruleValueInput.val(rule.value);
			this.$elem.find('tr.editing').removeClass('editing');
			$row.addClass('editing');
			this.$elem.find('tfoot tr').addClass('editing');
			
		}
		
	}
	
	/**
	 * Save a rule after editing
	 */
	saveRule() {
		
		const $selectedRule: JQuery         = this.$elem.find('tbody tr.editing'),
		      newRule: string               = this.$ruleSelector.val(),
		      newRuleValue: string | number = this.$ruleValueInput.val();
		
		if (!newRule) {
			this.showInvalidRuleMessage();
			return false;
		}
		
		// Save the rule as a data attribute of the first column.
		$selectedRule.find('td').eq(0).data('value', newRule).text( this.$ruleSelector.find('option:selected').text() );
		// Save the rule value as a data attribute of the second column.
		$selectedRule.find('td').eq(1).data('value', newRuleValue).text( newRuleValue );
		
		this.$elem.find('.editing').removeClass('editing');
		this.reset();
		this.updateRulesInput();
		
	}
	
	/**
	 * Show an alert when a invalid rule is being saved
	 */
	showInvalidRuleMessage() {
		
		this.globals.swal({
			title              : this.settings.get('selectRule'),
			text               : this.settings.get('youMustSelectARule'),
			type               : 'info',
			confirmButtonText  : this.settings.get('ok'),
			allowOutsideClick  : false
		});
		
	}
	
	/**
	 * Updates the hidden field with all the set up rules
	 */
	updateRulesInput() {
		
		let rules: any[] = [];
		
		this.$elem.find('tbody tr').each( (index: number, row: Element) => {
			
			rules.push( this.getRulefromRow( $(row) ) );
			
		});
		
		this.$rulesInput.val( JSON.stringify(rules) );
		
		this.$elem.trigger('atum-export-rules-input-updated', [rules]);
		
	}
	
	/**
	 * Get a rule from a table row's data
	 *
	 * @param {JQuery} $row
	 *
	 * @return {Rule}
	 */
	getRulefromRow($row: JQuery): Rule {
		
		return {
			condition: $row.children().eq(0).data('value'),  // The rule condition is on the first column.
			value    : $row.children().eq(1).data('value'),  // The value is on the second column.
		}
		
	}
	
	/**
	 * Reset the component's fields
	 */
	reset() {
		
		this.$ruleSelector.val('').change();
		this.$ruleValueInput.val('');
		
	}
	
	/**
	 * Check whether the current component field has rules set
	 *
	 * @return {boolean}
	 */
	hasRules(): boolean {
		return this.$rulesInput.val() !== '';
	}
	
}


// WEBPACK FOOTER //
// ./assets/js/src/components/export-central/_filters-builder.ts